import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { loadCurrentUser, logout } from "#root/redux/auth/actions";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  Link,
  useNavigate
} from "react-router-dom";
import {
  Button,
  Avatar,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Spinner
} from "@fluentui/react-components";
import {
  getIsAuthenticated,
  getIsCurrentUserLoading,
  getCurrentUser
} from "#root/redux/auth/selectors";
import { routes, errorRoutes } from "#root/routes/config";
import { generateRoutes } from "#root/routes/utils";
import "./styles.scss";
import { ROUTES } from "#root/routes/constants";
import { LOCAL_STORAGE_KEYS } from "#root/utils/localStorage";
import { ROLES } from "#root/utils/roles";
import { useTenantById } from "#root/queries/tenants"
import { Dismiss24Regular, SettingsFilled, LockClosedFilled, ArrowExitFilled, HomeFilled } from "@fluentui/react-icons";
import { useCurrentTenant } from "#root/queries/local";
import { showToast } from "#root/components/GlobalToaster";
import { requestResetPassword } from "#root/redux/auth/actions";
import { CurrentUser } from "#root/queries/users";
import { TENANT_TYPE } from "#root/utils/tenants";

const Layout: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isCurrentUserLoading = useSelector(getIsCurrentUserLoading);
  const currentUser = useSelector(getCurrentUser);
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { data: currentTenantId } = useCurrentTenant();
  const { data: tenantResponse } = useTenantById({ id: currentTenantId || '' });
  const currentTenant = tenantResponse?.item;

  const currentUserName = currentUser?.firstName && currentUser?.lastName
    ? currentUser?.firstName + ' ' + currentUser?.lastName
    : currentUser?.email;

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);

  useEffect(() => {
    if (isAuthenticated && !currentUser && !isCurrentUserLoading) {
      dispatch(loadCurrentUser());
    }
  }, [isAuthenticated, currentUser, isCurrentUserLoading, dispatch]);



  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const sidebar = document.querySelector('.sidebar');
      const avatarButton = document.querySelector('.layout__avatar-button');

      if (sidebar && !sidebar.contains(e.target as Node) &&
        avatarButton && !avatarButton.contains(e.target as Node)) {
        closeSidebar();
      }
    };

    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen]);

  useEffect(() => {
    if (isAuthenticated && currentUser && currentTenant && !isCurrentUserLoading) {
      if (currentUser.role && currentUser.role.toUpperCase() === ROLES.GLOBALADMIN) {
        const updatedUser: CurrentUser = {
          ...currentUser,
          language: currentTenant.language,
          unit: currentTenant.unit,
          tenantType: currentTenant.type || TENANT_TYPE.RAPID_REUNIFICATION
        };
        localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY, JSON.stringify(updatedUser));
      }
    }
  }, [isAuthenticated, currentUser, currentTenant, isCurrentUserLoading, dispatch]);


  const handleLogout = () => {
    setIsSidebarOpen(false);
    dispatch(logout());
  };

  const handleTenantLogout = () => {
    setIsSidebarOpen(false);
    // TODO: Refactor tenant logout
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TENANT_ID);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.VADMIN_SELECTED_INCIDENT);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.VTRACKER_SELECTED_INCIDENT);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.UNIT_ID);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.DEPARTMENT);
    window.location.replace(ROUTES.ROOT);
  };

  const handleUserSettings = () => {
    navigate(ROUTES.USER_SETTINGS);
  };

  const handleResetPassword = () => {
    if (currentUser?.email) {
      dispatch(
        requestResetPassword({
          email: currentUser.email,
          tenantId: localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT_ID) || "",
        })
      );
      showToast("Reset Password Email Has Been Sent", "success");
    }
    closeSidebar();
  };

  const getLayoutTitle = () => {
    if (location.pathname.startsWith(ROUTES.VADMIN.ROOT)) return 'VAdmin';
    if (location.pathname.startsWith(ROUTES.VTRACKER.ROOT)) return 'VTracker';

    const isMerrge211 = currentTenant?.type === TENANT_TYPE.MERRGE_211 ||
      location.pathname.startsWith(ROUTES.RESPONDER_REGISTRATION) ||
      location.pathname.startsWith(ROUTES.MERRGE211.ROOT);

    return (
      <div className="layout__title-container">
        <div className="layout__title-main">
          {isMerrge211 ? 'EMC MERRGE™ 211' : 'EMC MERRGE ™'}
        </div>
        <div className="layout__title-subtitle">Mass Emergency Response Reunification Global Efforts</div>
      </div>
    );
  };

  const isGlobalAdmin = currentUser?.role?.toUpperCase() === ROLES.GLOBALADMIN;
  const hasTenantId = !!localStorage.getItem('tenantId');

  const isPublicPage = location.pathname.startsWith(ROUTES.PUBLIC_REQUEST) ||
    location.pathname.startsWith(ROUTES.FAMILY_ASSISTANCE_ENTRY) ||
    location.pathname.startsWith(ROUTES.RESPONDER_REGISTRATION);

  return (
    <div className="layout">
      <header className="layout__header">
        <div className="layout__header-left">
          {isPublicPage ? (
            <div className="layout__logo">
              <img src="/images/emc-logo.svg" alt="EMC Logo" />
            </div>
          ) : (
            <Link to={ROUTES.ROOT} className="layout__logo">
              <img src="/images/emc-logo.svg" alt="EMC Logo" />
            </Link>
          )}
        </div>
        <div className="layout__header-center">
          <div className="layout__title">{getLayoutTitle()}</div>
        </div>
        <div className="layout__header-right">
          {isAuthenticated && currentUser && !isPublicPage && (
            <Button
              size="large"
              appearance="transparent"
              className="layout__avatar-button"
              icon={<Avatar size={48} name={currentUserName} />}
              onClick={toggleSidebar}
            />
          )}
        </div>

        <Drawer
          open={isSidebarOpen && !isPublicPage}
          onOpenChange={(_, { open }) => !isPublicPage && setIsSidebarOpen(open)}
          position="end"
          className="sidebar"
          separator
        >
          <DrawerHeader className="sidebar-header">
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={() => setIsSidebarOpen(false)}
                />
              }
            >
              <div className="user-info">
                <Avatar className="user-avatar"
                  size={48}
                  color="neutral"
                  name={currentUserName}
                />
                <div>
                  {currentUser?.firstName && currentUser?.lastName
                    ? <p className="user-name" title={currentUser?.firstName + " " + currentUser?.lastName}>{currentUser?.firstName + " " + currentUser?.lastName}</p>
                    : ""}
                  <p className="user-email" title={currentUser?.email}>{currentUser?.email}</p>
                  {
                    currentTenant?.organizationName && <p className="user-organization" title={currentTenant?.organizationName}>{currentTenant?.organizationName}</p>
                  }
                </div>
              </div>
            </DrawerHeaderTitle>
          </DrawerHeader>

          <DrawerBody className="sidebar-body">
            {isGlobalAdmin && hasTenantId && (
              <Button
                icon={<HomeFilled />}
                appearance="transparent"
                size="large"
                onClick={() => {
                  handleTenantLogout();
                  closeSidebar();
                }}
              >
                Super Admin Home
              </Button>
            )}
            <Button
              icon={<SettingsFilled />}
              appearance="transparent"
              size="large"
              onClick={() => {
                handleUserSettings();
                closeSidebar();
              }}
            >
              User Settings
            </Button>
            <Button
              icon={<LockClosedFilled />}
              appearance="transparent"
              size="large"
              onClick={() => {
                handleResetPassword();
              }}
            >
              Reset Password
            </Button>
            <Button
              icon={<ArrowExitFilled />}
              appearance="transparent"
              size="large"
              onClick={() => {
                handleLogout();
                closeSidebar();
              }}
            >
              Log Out
            </Button>
          </DrawerBody>
        </Drawer>

      </header>
      {(isCurrentUserLoading || (isAuthenticated && !currentUser)) ? (
        <div className="layout__loading"><Spinner size="extra-large" /></div>
      ) : (
        <div className="layout__body layout__body--paddings">
          <Routes>
            {generateRoutes({
              routes,
              userRole: currentUser?.role,
              isAuthenticated
            })}
            {generateRoutes({
              routes: errorRoutes,
              userRole: currentUser?.role,
              isAuthenticated
            })}
            <Route path="*" element={<Navigate to={ROUTES.ERROR_404} replace />} />
          </Routes>
        </div>)}
    </div>
  );
};

export default Layout;
